import axios from 'axios';
import { shouldUseMockData, mockApi } from './mockApi';
import { config } from '../config/environment';

// Type definitions for API responses
interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  message: string;
  timestamp?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface PatientsApiResponse {
  success: boolean;
  patients: any[];
  message: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface ApiErrorResponse {
  success: false;
  message: string;
  timestamp?: string;
  data?: never; // Explicitly mark data as never for error responses
}

type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

const API_BASE_URL = config.apiBaseUrl;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for session-based auth
  validateStatus: function (status) {
    // Only treat 2xx as successful, everything else as error
    // This ensures 401, 403, 404, etc. trigger the error interceptor
    return status >= 200 && status < 300;
  }
});

// Enhanced API service with mock data support
export const enhancedApi = {
  // Authentication endpoints
  auth: {
    login: async (username: string, password: string): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        console.log('🔧 Using MOCK API for login');
        return await mockApi.auth.login(username, password);
      }
      console.log('🔧 Using REAL API for login');
      const response = await api.post('/auth.php?action=login', { username, password });
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    logout: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.auth.logout();
      }
      const response = await api.post('/auth.php?action=logout');
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    getCurrentUser: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.auth.getCurrentUser();
      }
      const response = await api.get('/auth.php?action=me');
      return {
        success: response.data.success,
        data: response.data.user || response.data,
        message: response.data.message
      };
    }
  },

  // Dashboard endpoints
  dashboard: {
    getStats: async () => {
      if (shouldUseMockData()) {
        return await mockApi.dashboard.getStats();
      }
      const response = await api.get('/dashboard.php?action=stats');
      return {
        success: response.data.success,
        data: response.data.stats || response.data,
        message: response.data.message
      };
    },
    
    getRecentAudits: async (params?: any) => {
      if (shouldUseMockData()) {
        return await mockApi.dashboard.getRecentAudits(params);
      }
      const response = await api.get('/dashboard.php?action=recent_audits', { params });
      return {
        success: response.data.success,
        data: response.data.audits || response.data,
        message: response.data.message,
        pagination: response.data.pagination
      };
    }
  },

  // Patients endpoints
  patients: {
    getPatients: async (params?: any): Promise<PatientsApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.patients.getPatients(params);
      }
      const response = await api.get('/admin.php?action=patients', { params });
      return {
        success: response.data.success,
        patients: response.data.patients || [],
        message: response.data.message,
        pagination: response.data.pagination
      };
    },

    createMortalityCase: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.patients.createMortalityCase(data);
      }
      const response = await api.post('/admin.php?action=create_mortality_case', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    getPatient: async (id: string) => {
      if (shouldUseMockData()) {
        return await mockApi.patients.getPatient(id);
      }
      const response = await api.get(`/admin.php?action=patient&id=${id}`);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    createMorbidityCase: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.patients.createMorbidityCase(data);
      }
      const response = await api.post('/admin.php?action=create_morbidity_case', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    updatePatient: async (id: number, data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.patients.updatePatient(id, data);
      }
      const response = await api.put('/admin.php?action=update_patient', { id, ...data });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    }
  },

  // Morbidity Categories endpoints
  morbidityCategories: {
    getCategories: async () => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityCategories.getCategories();
      }
      const response = await api.get('/morbidity-categories.php?action=list');
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },
    
    createCategory: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityCategories.createCategory(data);
      }
      const response = await api.post('/morbidity-categories.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },
    
    updateCategory: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityCategories.updateCategory(data);
      }
      const response = await api.put('/morbidity-categories.php?action=update', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },
    
    deleteCategory: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityCategories.deleteCategory(data);
      }
      const response = await api.delete('/morbidity-categories.php?action=delete', { data });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    }
  },

  // Morbidity Audit Indications endpoints
  morbidityAuditIndications: {
    getIndications: async () => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityAuditIndications.getIndications();
      }
      const response = await api.get('/morbidity-audit-indications.php?action=list');
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },
    
    createIndication: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityAuditIndications.createIndication(data);
      }
      const response = await api.post('/morbidity-audit-indications.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },
    
    updateIndication: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityAuditIndications.updateIndication(data);
      }
      const response = await api.put('/morbidity-audit-indications.php?action=update', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },
    
    deleteIndication: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.morbidityAuditIndications.deleteIndication(data);
      }
      const response = await api.delete('/morbidity-audit-indications.php?action=delete', { data });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    }
  },

  // Admin endpoints
  admin: {
    assignAudits: async (assignmentData: any) => {
      if (shouldUseMockData()) {
        return await mockApi.users.assignAudits(assignmentData);
      }
      const response = await api.post('/admin.php?action=assign_audits', assignmentData);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    getEMRSyncStatus: async () => {
      if (shouldUseMockData()) {
        return await mockApi.admin.getEMRSyncStatus();
      }
      const response = await api.get('/admin.php?action=emr_sync_status');
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },
    
    smartEMRSync: async (syncData: any) => {
      if (shouldUseMockData()) {
        return await mockApi.admin.smartEMRSync(syncData);
      }
      const response = await api.post('/admin.php?action=smart_emr_sync', syncData);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    getEnhancedEMRData: async (month: number, year: number) => {
      if (shouldUseMockData()) {
        return await mockApi.admin.getEnhancedEMRData(month, year);
      }
      const response = await api.get(`/admin.php?action=enhanced_emr_import&month=${month}&year=${year}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    importSelectedPatients: async (patients: any[]) => {
      if (shouldUseMockData()) {
        return await mockApi.admin.importSelectedPatients(patients);
      }
      const response = await api.post('/admin.php?action=import_selected_patients', { patients });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    updatePatientAuditRequired: async (patientId: number, isAuditRequired: boolean) => {
      if (shouldUseMockData()) {
        return await mockApi.admin.updatePatientAuditRequired(patientId, isAuditRequired);
      }
      const response = await api.post('/admin.php?action=update_patient_audit_required', {
        patient_id: patientId,
        is_audit_required: isAuditRequired
      });
      return {
        success: response.data.success,
        message: response.data.message
      };
    },
    
    updateMorbidityCaseAuditRequired: async (caseId: number, isAuditRequired: boolean) => {
      if (shouldUseMockData()) {
        return await mockApi.admin.updateMorbidityCaseAuditRequired(caseId, isAuditRequired);
      }
      const response = await api.post('/admin.php?action=update_morbidity_case_audit_required', {
        case_id: caseId,
        is_audit_required: isAuditRequired
      });
      return {
        success: response.data.success,
        message: response.data.message
      };
    },
    
    deleteMortalityCase: async (caseId: number) => {
      if (shouldUseMockData()) {
        return await mockApi.admin.deleteMortalityCase(caseId);
      }
      const response = await api.post('/admin.php?action=delete_mortality_case', {
        case_id: caseId
      });
      return {
        success: response.data.success,
        message: response.data.message
      };
    },
    
    deleteMorbidityCase: async (caseId: number) => {
      if (shouldUseMockData()) {
        return { success: false, message: 'Mock not implemented for deleteMorbidityCase' };
      }
      const response = await api.post('/admin.php?action=delete_morbidity_case', {
        case_id: caseId
      });
      return {
        success: response.data.success,
        message: response.data.message
      };
    },
  },

  // Users endpoints

  // Audit endpoints
  audit: {
    getAudits: async () => {
      if (shouldUseMockData()) {
        return await mockApi.audit.getAudits();
      }
      const response = await api.get('/audit.php?action=list');
      return {
        success: response.data.success,
        data: response.data.audits || response.data,
        message: response.data.message
      };
    },

    getMyAudits: async (params?: any) => {
      if (shouldUseMockData()) {
        return await mockApi.audit.getMyAudits(params);
      }
      const response = await api.get('/audit.php?action=my_audits', { params });
      return {
        success: response.data.success,
        data: response.data.data || [],
        message: response.data.message,
        pagination: response.data.pagination
      };
    },
    
    getAudit: async (id: string) => {
      if (shouldUseMockData()) {
        return await mockApi.audit.getAudit(id);
      }
      const response = await api.get(`/audit.php?action=get&id=${id}`);
      return {
        success: response.data.success,
        data: {
          // For SupervisorReview component
          audit: response.data.assignment,
          patient: response.data.patient,
          form_data: response.data.form,
          // For AuditForm component (backward compatibility)
          form: response.data.form,
          patient_data: response.data.patient
        },
        message: response.data.message
      };
    },
    
    submitAudit: async (id: string, auditData: any) => {
      if (shouldUseMockData()) {
        return await mockApi.audit.submitAudit(auditData);
      }
      const response = await api.post(`/audit.php?action=submit_audit&id=${id}`, auditData);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    saveDraft: async (id: string, auditData: any) => {
      if (shouldUseMockData()) {
        return await mockApi.audit.saveDraft(auditData);
      }
      const response = await api.post(`/audit.php?action=save_draft&id=${id}`, auditData);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },

    updateAudit: async (auditData: any) => {
      if (shouldUseMockData()) {
        return await mockApi.audit.updateAudit(auditData);
      }
      const response = await api.put('/audit.php?action=update', auditData);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    approveAudit: async (auditId: string, reviewNotes: string) => {
      if (shouldUseMockData()) {
        return await mockApi.audit.approveAudit(auditId, reviewNotes);
      }
      const response = await api.post('/review.php?action=approve', { auditId, reviewNotes });
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    rejectAudit: async (auditId: string, reviewNotes: string) => {
      if (shouldUseMockData()) {
        return await mockApi.audit.rejectAudit(auditId, reviewNotes);
      }
      const response = await api.post('/review.php?action=reject', { auditId, reviewNotes });
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    }
  },


  // Notifications endpoints
  notifications: {
    getNotifications: async (params?: any) => {
      if (shouldUseMockData()) {
        return await mockApi.notifications.getNotifications(params);
      }
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page);
      if (params?.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(`/notifications.php?action=list&${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getUnreadCount: async () => {
      if (shouldUseMockData()) {
        return await mockApi.notifications.getUnreadCount();
      }
      const response = await api.get('/notifications.php?action=unread_count');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    markAsRead: async (notificationId: number) => {
      if (shouldUseMockData()) {
        return await mockApi.notifications.markAsRead(notificationId);
      }
      const response = await api.post('/notifications.php?action=mark_read', {
        notification_id: notificationId
      });
      return {
        success: response.data.success,
        message: response.data.message
      };
    },

    markAllAsRead: async () => {
      if (shouldUseMockData()) {
        return await mockApi.notifications.markAllAsRead();
      }
      const response = await api.post('/notifications.php?action=mark_all_read');
      return {
        success: response.data.success,
        message: response.data.message
      };
    }
  },

  // Audit Logs endpoints
  auditLogs: {
    getAuditLogs: async (params?: any) => {
      if (shouldUseMockData()) {
        return await mockApi.auditLogs.getAuditLogs(params);
      }
      const queryParams = new URLSearchParams();
      if (params?.user_id) queryParams.append('user_id', params.user_id);
      if (params?.action_type) queryParams.append('action_type', params.action_type);
      if (params?.date_from) queryParams.append('date_from', params.date_from);
      if (params?.date_to) queryParams.append('date_to', params.date_to);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.page) queryParams.append('page', params.page);
      if (params?.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(`/audit-logs.php?action=list&${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message
      };
    },

    getAuditLog: async (id: number) => {
      if (shouldUseMockData()) {
        return await mockApi.auditLogs.getAuditLog(id);
      }
      const response = await api.get(`/audit-logs.php?action=get&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    createAuditLog: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.auditLogs.createAuditLog(data);
      }
      const response = await api.post('/audit-logs.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    bulkCreateAuditLogs: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.auditLogs.bulkCreateAuditLogs(data);
      }
      const response = await api.post('/audit-logs.php?action=bulk_create', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    deleteAuditLog: async (id: number) => {
      if (shouldUseMockData()) {
        return await mockApi.auditLogs.deleteAuditLog(id);
      }
      const response = await api.delete(`/audit-logs.php?action=delete&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    clearOldAuditLogs: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.auditLogs.clearOldAuditLogs(data);
      }
      const response = await api.delete('/audit-logs.php?action=clear_old', { data });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    exportAuditLogs: async (params?: any) => {
      if (shouldUseMockData()) {
        return await mockApi.auditLogs.exportAuditLogs(params);
      }
      const queryParams = new URLSearchParams();
      if (params?.format) queryParams.append('format', params.format);
      if (params?.date_from) queryParams.append('date_from', params.date_from);
      if (params?.date_to) queryParams.append('date_to', params.date_to);
      
      const response = await api.get(`/audit-logs.php?action=export&${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data,
        export_info: response.data.export_info,
        message: response.data.message
      };
    }
  },


  // EMR endpoints
  emr: {
    fetchPatient: async (patientId: string) => {
      if (shouldUseMockData()) {
        return await mockApi.emr.fetchPatient(patientId);
      }
      const response = await api.get(`/emr.php?action=fetch_patient&patient_id=${patientId}`);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    fetchMortalityCases: async () => {
      if (shouldUseMockData()) {
        return await mockApi.emr.fetchMortalityCases();
      }
      const response = await api.get('/emr.php?action=fetch_mortality_cases');
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    syncPatient: async (patientId: string) => {
      if (shouldUseMockData()) {
        return await mockApi.emr.syncPatient(patientId);
      }
      const response = await api.get(`/emr.php?action=sync_patient&patient_id=${patientId}`);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },
    
    syncAll: async () => {
      if (shouldUseMockData()) {
        return await mockApi.emr.syncAll();
      }
      const response = await api.get('/emr.php?action=sync_all');
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    }
  },
  
  // Departments endpoints
  departments: {
    getDepartments: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.departments.getDepartments();
      }
      const response = await api.get('/departments.php?action=list');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    getDepartment: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.departments.getDepartment(id);
      }
      const response = await api.get(`/departments.php?action=get&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    createDepartment: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.departments.createDepartment(data);
      }
      const response = await api.post('/departments.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    updateDepartment: async (id: number, data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.departments.updateDepartment(id, data);
      }
      const response = await api.put(`/departments.php?action=update&id=${id}`, data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    deleteDepartment: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.departments.deleteDepartment(id);
      }
      const response = await api.delete(`/departments.php?action=delete&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    }
  },

  // Units endpoints
  units: {
    getUnits: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.units.getUnits();
      }
      const response = await api.get('/units.php?action=list');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    getUnitsByDepartment: async (departmentId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.units.getUnitsByDepartment(departmentId);
      }
      const response = await api.get(`/units.php?action=by_department&department_id=${departmentId}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    getUnit: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.units.getUnit(id);
      }
      const response = await api.get(`/units.php?action=get&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    createUnit: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.units.createUnit(data);
      }
      const response = await api.post('/units.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    updateUnit: async (id: number, data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.units.updateUnit(id, data);
      }
      const response = await api.put(`/units.php?action=update&id=${id}`, data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    deleteUnit: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.units.deleteUnit(id);
      }
      const response = await api.delete(`/units.php?action=delete&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    }
  },

  // Recommendations endpoints
  recommendations: {
    getCategories: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendations.getCategories();
      }
      const response = await api.get('/recommendations.php?action=get_categories');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    createRecommendation: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendations.createRecommendation(data);
      }
      const response = await api.post('/recommendations.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    updateRecommendation: async (id: number, data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendations.updateRecommendation(id, data);
      }
      const response = await api.put(`/recommendations.php?action=update&id=${id}`, data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },
    
    getRecommendations: async (auditFormId?: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendations.getRecommendations(auditFormId);
      }
      const url = auditFormId 
        ? `/recommendations.php?action=get&audit_form_id=${auditFormId}`
        : '/recommendations.php?action=get';
      const response = await api.get(url);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    }
  },

  // Users Management endpoints
  users: {
    getUsers: async (params?: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.getUsers(params);
      }
      const queryParams = new URLSearchParams();
      if (params?.role) queryParams.append('role', params.role);
      if (params?.department_id) queryParams.append('department_id', params.department_id);
      if (params?.unit_id) queryParams.append('unit_id', params.unit_id);
      if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active);
      if (params?.search) queryParams.append('search', params.search);
      
      const response = await api.get(`/users.php?action=list&${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getUser: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.getUser(id);
      }
      const response = await api.get(`/users.php?action=get&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getUserProfile: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.getUserProfile();
      }
      const response = await api.get('/users.php?action=profile');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    createUser: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.createUser(data);
      }
      const response = await api.post('/users.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    updateUser: async (id: number, data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.updateUser(id, data);
      }
      const response = await api.post('/users.php?action=update', { id, ...data });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    changePassword: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.changePassword(data);
      }
      const response = await api.post('/users.php?action=change_password', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    toggleUserStatus: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.toggleUserStatus(data);
      }
      const response = await api.post('/users.php?action=toggle_status', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    deleteUser: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.users.deleteUser(id);
      }
      const response = await api.delete(`/users.php?action=delete&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    }
  },

  // Review Management endpoints
  reviews: {
    getReviews: async (params?: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.getReviews(params);
      }
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.reviewer_id) queryParams.append('reviewer_id', params.reviewer_id);
      if (params?.date_from) queryParams.append('date_from', params.date_from);
      if (params?.date_to) queryParams.append('date_to', params.date_to);
      
      const response = await api.get(`/review.php?action=list&${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: {
          reviews: response.data.reviews,
          pagination: response.data.pagination
        },
        message: response.data.message
      };
    },

    getReview: async (id: number) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.getReview(id);
      }
      const response = await api.get(`/review.php?action=get&id=${id}`);
      return {
        success: response.data.success,
        data: {
          audit: response.data.audit,
          patient: response.data.patient,
          form_data: response.data.form_data
        },
        message: response.data.message
      };
    },

    getMyReviews: async () => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.getMyReviews();
      }
      const response = await api.get('/review.php?action=my_reviews');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getPendingReviews: async () => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.getPendingReviews();
      }
      const response = await api.get('/review.php?action=pending');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getReviewStatistics: async () => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.getReviewStatistics();
      }
      const response = await api.get('/review.php?action=statistics');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    submitReview: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.submitReview(data);
      }
      const response = await api.post('/review.php?action=submit', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    assignReview: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.assignReview(data);
      }
      const response = await api.post('/review.php?action=assign', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    reassignReview: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.reassignReview(data);
      }
      const response = await api.post('/review.php?action=reassign', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    updateReview: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.updateReview(data);
      }
      const response = await api.put('/review.php?action=update', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    approveReview: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.approveReview(data);
      }
      const response = await api.put('/review.php?action=approve', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    rejectReview: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.rejectReview(data);
      }
      const response = await api.put('/review.php?action=reject', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    deleteReview: async (id: number) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.deleteReview(id);
      }
      const response = await api.delete(`/review.php?action=delete&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getReviewers: async (auditFormId: number) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.getReviewers(auditFormId);
      }
      const response = await api.get(`/review.php?action=get_reviewers&audit_form_id=${auditFormId}`);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },

    submitIndividualReview: async (data: any) => {
      if (shouldUseMockData()) {
        return await mockApi.reviews.submitIndividualReview(data);
      }
      const response = await api.post('/review.php?action=submit_individual', data);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    }
  },

  // Team Management endpoints
  teams: {
    getTeams: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.getTeams();
      }
      const response = await api.get('/teams.php?action=list');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getTeam: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.getTeam(id);
      }
      const response = await api.get(`/teams.php?action=get&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    createTeam: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.createTeam(data);
      }
      const response = await api.post('/teams.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    updateTeam: async (id: number, data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.updateTeam(id, data);
      }
      const response = await api.post('/teams.php?action=update', { id, ...data });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    deleteTeam: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.deleteTeam(id);
      }
      const response = await api.delete(`/teams.php?action=delete&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getTeamMembers: async (teamId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.getTeamMembers(teamId);
      }
      const response = await api.get(`/teams.php?action=members&team_id=${teamId}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    addTeamMember: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.addTeamMember(data);
      }
      const response = await api.post('/teams.php?action=add_member', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    removeTeamMember: async (memberId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.removeTeamMember(memberId);
      }
      const response = await api.post('/teams.php?action=remove_member', { member_id: memberId });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    getAvailableUsers: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.teams.getAvailableUsers();
      }
      const response = await api.get('/teams.php?action=users');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    }
  },

  // Assignment Management endpoints
  assignments: {
    getAssignments: async (params?: any) => {
      if (shouldUseMockData()) {
        return await mockApi.assignments.getAssignments(params);
      }
      const response = await api.get('/admin.php?action=assignments', { params });
      return {
        success: response.data.success,
        data: response.data.data || [],
        pagination: response.data.pagination,
        message: response.data.message
      };
    },

    getAssignmentAuditors: async (assignmentId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.assignments.getAssignmentAuditors(assignmentId);
      }
      const response = await api.get(`/admin.php?action=get_assignment_auditors&assignment_id=${assignmentId}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    updateAuditorStatus: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.assignments.updateAuditorStatus(data);
      }
      const response = await api.put('/admin.php?action=update_auditor_status', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    updateAssignment: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.assignments.updateAssignment(data);
      }
      const response = await api.put('/admin.php?action=update_assignment', data);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    },

    deleteAssignment: async (assignmentId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.assignments.deleteAssignment(assignmentId);
      }
      const response = await api.delete(`/admin.php?action=delete_assignment&id=${assignmentId}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    }
  },

  // Revision History endpoints
  revisionHistory: {
    getAssignmentRevisions: async (assignmentId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.revisionHistory.getAssignmentRevisions(assignmentId);
      }
      const response = await api.get(`/revision_history.php?action=assignment&assignment_id=${assignmentId}`);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },

    getRevisionHistory: async (params?: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.revisionHistory.getRevisionHistory(params);
      }
      const queryParams = new URLSearchParams();
      if (params?.assignment_id) queryParams.append('assignment_id', params.assignment_id);
      if (params?.reviewer_id) queryParams.append('reviewer_id', params.reviewer_id);
      if (params?.assignment_status) queryParams.append('assignment_status', params.assignment_status);
      if (params?.date_from) queryParams.append('date_from', params.date_from);
      if (params?.date_to) queryParams.append('date_to', params.date_to);
      if (params?.page) queryParams.append('page', params.page);
      if (params?.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(`/revision_history.php?action=list&${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },

    getRevisionDetails: async (revisionId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.revisionHistory.getRevisionDetails(revisionId);
      }
      const response = await api.get(`/revision_history.php?action=get&id=${revisionId}`);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    },

    createRevisionEntry: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.revisionHistory.createRevisionEntry(data);
      }
      const response = await api.post('/revision_history.php?action=create', data);
      return {
        success: response.data.success,
        data: response.data,
        message: response.data.message
      };
    }
  },

  // PDF Export endpoints
  pdfExport: {
    exportAuditForm: async (auditFormId: number) => {
      if (shouldUseMockData()) {
        // For mock data, we'll simulate a download
        const link = document.createElement('a');
        link.href = 'data:text/html;charset=utf-8,<html><body><h1>Mock PDF Export</h1><p>This would be a PDF export for audit form ' + auditFormId + '</p></body></html>';
        link.download = `Audit_${auditFormId}_Mock_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        return { success: true, message: 'Mock PDF export initiated' };
      }
      
      try {
        // Open PDF in new window for printing/saving
        const url = `${api.defaults.baseURL}/pdf_export.php?action=audit_form&audit_form_id=${auditFormId}`;
        const printWindow = window.open(url, '_blank');
        
        if (printWindow) {
          // Wait for the content to load, then trigger print dialog
          printWindow.onload = function() {
            setTimeout(() => {
              printWindow.print();
            }, 1000);
          };
          return { success: true, message: 'PDF export window opened. Use browser print to save as PDF.' };
        } else {
          return { success: false, message: 'Failed to open export window. Please allow popups and try again.' };
        }
      } catch (error) {
        console.error('PDF export error:', error);
        return { success: false, message: 'Failed to export PDF' };
      }
    }
  },

  // Recommendation Follow-up endpoints
  recommendationFollowup: {
    getDashboard: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.getDashboard();
      }
      const response = await api.get('/recommendation_followup.php?action=dashboard');
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    getRecommendations: async (params: string): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.getRecommendations(params);
      }
      const response = await api.get(`/recommendation_followup.php?action=list&${params}`);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message,
        pagination: response.data.pagination
      };
    },

    getRecommendationDetails: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.getRecommendationDetails(id);
      }
      const response = await api.get(`/recommendation_followup.php?action=details&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    updateStatus: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.updateStatus(data);
      }
      const response = await api.post('/recommendation_followup.php?action=update_status', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    assignRecommendation: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.assignRecommendation(data);
      }
      const response = await api.post('/recommendation_followup.php?action=assign', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    createFollowup: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.createFollowup(data);
      }
      const response = await api.post('/recommendation_followup.php?action=create_followup', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    getFollowups: async (recommendationId: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.getFollowups(recommendationId);
      }
      const response = await api.get(`/recommendation_followup.php?action=followups&recommendation_id=${recommendationId}`);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    updateRecommendation: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.updateRecommendation(data);
      }
      const response = await api.put('/recommendation_followup.php?action=update', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    bulkUpdate: async (data: any): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.bulkUpdate(data);
      }
      const response = await api.post('/recommendation_followup.php?action=bulk_update', data);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    getOverdueRecommendations: async (): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.getOverdueRecommendations();
      }
      const response = await api.get('/recommendation_followup.php?action=overdue');
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    deleteRecommendation: async (id: number): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.deleteRecommendation(id);
      }
      const response = await api.delete(`/recommendation_followup.php?action=delete&id=${id}`);
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    },

    bulkDeleteRecommendations: async (recommendationIds: number[]): Promise<ApiResponse> => {
      if (shouldUseMockData()) {
        return await mockApi.recommendationFollowup.bulkDeleteRecommendations(recommendationIds);
      }
      const response = await api.delete('/recommendation_followup.php?action=bulk_delete', {
        data: { recommendation_ids: recommendationIds }
      });
      return {
        success: response.data.success,
        data: response.data.data || response.data,
        message: response.data.message
      };
    }
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log('🔍 API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`
    });
    // Add any auth headers if needed
    return config;
  },
  (error) => {
    console.log('🔍 API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('🔍 API Response Success:', {
      status: response.status,
      url: response.config?.url,
      method: response.config?.method
    });
    return response;
  },
  (error) => {
    console.log('🔍 API Response Error:', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      data: error.response?.data
    });
    
    // Handle all 401 errors consistently
    if (error.response?.status === 401) {
      // Use basename from centralized config
      const loginPath = `${config.basename}/login`;
      
      // DEBUG: Log all path information to find root cause
      console.log('🔍 DEBUG 401 Handler - Path Analysis:', {
        'config.basename': JSON.stringify(config.basename),
        'config.basename.length': config.basename.length,
        'config.basename.hasSpaces': config.basename.includes(' '),
        'loginPath': JSON.stringify(loginPath),
        'loginPath.length': loginPath.length,
        'window.location.pathname': JSON.stringify(window.location.pathname),
        'window.location.pathname.length': window.location.pathname.length,
        'window.location.href': window.location.href,
        'decoded pathname': JSON.stringify(decodeURIComponent(window.location.pathname))
      });
      
      // Decode URL to handle encoded spaces and special characters
      const currentPath = decodeURIComponent(window.location.pathname);
      const normalizedLoginPath = decodeURIComponent(loginPath);
      
      // Check if we're already on the login page (handle URL encoding variations)
      if (currentPath === normalizedLoginPath || 
          currentPath.endsWith('/login') || 
          currentPath.includes('/login')) {
        console.log('ℹ️ Already on login page, not redirecting');
        return Promise.reject(error);
      }
      
      console.log('🚨 401 Unauthorized detected! Redirecting to login...');
      
      // Clear any existing user state
      localStorage.removeItem('user');
      sessionStorage.clear();
      
      console.log('🧹 Cleared localStorage and sessionStorage');
      
      // Redirect to login
      console.log(`🔄 Redirecting to ${loginPath}`);
      window.location.href = loginPath;
    } else {
      console.log('ℹ️ Non-401 error, not redirecting');
    }
    
    return Promise.reject(error);
  }
);

// Debug function to test 401 handling
(window as any).test401Redirect = async () => {
  console.log('🧪 Testing 401 redirect...');
  try {
    // Make a request to an endpoint that should return 401
    await api.get('/test-401-endpoint');
  } catch (error) {
    console.log('🧪 Test completed - check if redirect happened');
  }
};

export default api;
